/**
 * SiteFooter — the full site footer (logo + tagline, Product column, Legal
 * column, disclaimer, copyright). Shared by the marketing pages, the signed-in
 * employer app, and the employee portal so every surface shows the same
 * footer (Bill's 2026-08-03 request: the pre-login footer everywhere).
 *
 * @module components/SiteFooter
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';

export function SiteFooter(): ReactElement {
  return (
    <footer className="border-t border-gray-100 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row">
          <div>
            <Logo size="xs" />
            <p className="mt-2 max-w-xs text-sm text-gray-500">
              Focused payroll for small teams.
            </p>
          </div>
          <div className="flex gap-12 text-sm">
            <div>
              <div className="font-semibold text-gray-800">Product</div>
              {/* Two columns under the title keeps the footer compact (Bill's call) */}
              <ul className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1.5 text-gray-500">
                <li><Link to="/features" className="hover:text-gray-900">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-gray-900">Pricing</Link></li>
                <li><Link to="/faq" className="hover:text-gray-900">FAQ</Link></li>
                <li><Link to="/resources" className="hover:text-gray-900">Resources</Link></li>
                <li><Link to="/guides" className="hover:text-gray-900">Guides</Link></li>
                <li><Link to="/login" className="hover:text-gray-900">Sign in</Link></li>
                <li><Link to="/register" className="hover:text-gray-900">Create account</Link></li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-gray-800">Legal</div>
              <ul className="mt-2 space-y-1.5 text-gray-500">
                <li><Link to="/terms" className="hover:text-gray-900">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-gray-900">Privacy Policy</Link></li>
                <li><Link to="/acceptable-use" className="hover:text-gray-900">Acceptable Use</Link></li>
                <li><Link to="/restricted-businesses" className="hover:text-gray-900">Restricted Businesses</Link></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-8 border-t border-gray-100 pt-6">
          <p className="text-xs text-gray-500">
            For informational purposes only. Consult a tax professional before making payroll decisions.
          </p>
          <p className="mt-2 text-xs text-gray-500">© 2026 Ballast Payroll. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
